import { useState } from "react";
import { projectsData, type Project } from "@/mocks/projects";

interface Service {
  id: string;
  title: string;
  titleEn: string;
  desc: string;
  descEn: string;
  tags: string[];
  tagsEn: string[];
  image: string;
  relatedProjectIds: number[];
}

const services: Service[] = [
  {
    id: "01",
    title: "零售设计",
    titleEn: "Retail Design",
    desc: "从品牌基因出发，重新定义零售空间的消费者体验设计。我们深度介入陈列逻辑、动线规划与材质选择，让每一家门店都成为品牌最有力的表达载体。",
    descEn: "Starting from brand DNA, we redefine the consumer experience within retail spaces. We engage deeply with display logic, circulation planning, and material selection — making every store the most powerful expression of its brand.",
    tags: ["门店设计", "陈列规划", "体验设计", "品牌设计"],
    tagsEn: ["Store Design", "Display Planning", "Experience Design", "Brand Design"],
    image: "https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/b1640f907391c75c75f9186d0ecab340.jpeg",
    relatedProjectIds: [6, 11, 12, 13, 7],
  },
  {
    id: "02",
    title: "品牌空间及标准设计",
    titleEn: "Brand Space & Standard Design",
    desc: "将品牌的价值观与视觉语言转化为可感知的空间叙事。我们为品牌旗舰店、展厅、快闪空间提供从概念到落地的全链路设计服务。",
    descEn: "We translate brand values and visual language into tangible spatial narratives. From flagship stores and showrooms to pop-up spaces, we deliver end-to-end design services from concept to completion.",
    tags: ["旗舰店", "展厅设计", "快闪空间", "品牌叙事及SI标准"],
    tagsEn: ["Flagship Store", "Showroom Design", "Pop-up Space", "Brand Narrative & SI Standard"],
    image: "https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/4a006bda32cf24c054f8787ef75503bd.jpeg",
    relatedProjectIds: [5, 8, 9, 10],
  },
  {
    id: "03",
    title: "商业设计",
    titleEn: "Commercial Design",
    desc: "涵盖商业、办公、文娱等多元商业业态，以精准的功能布局与高完成度的设计细节，为商业项目创造持久的竞争力与空间价值。",
    descEn: "Spanning commercial complexes, offices, and cultural entertainment venues, we deliver precise functional layouts and refined design details that create lasting competitive advantage and spatial value for commercial projects.",
    tags: ["商业综合体", "办公空间", "文娱场所", "功能规划"],
    tagsEn: ["Commercial Complex", "Office Space", "Entertainment Venue", "Functional Planning"],
    image: "https://public.readdy.ai/ai/img_res/2e649b2a39211f82d5dda55b8e92cd69.jpg",
    relatedProjectIds: [2],
  },
  {
    id: "04",
    title: "设计管理",
    titleEn: "Design Management",
    desc: "作为设计顾问，我们为品牌及海外设计团队提供从设计概念、方案深化、材料、灯光、MEP、家具软装道具设计及评审到施工监督的全程管理服务，确保设计意图在每一个执行环节中得到忠实呈现。",
    descEn: "Acting as design consultant for brands and overseas design teams, we provide end-to-end management services — from design concept and scheme development, materials, lighting, MEP, furniture and prop design & review, through to construction supervision — ensuring design intent is faithfully realized at every stage of execution.",
    tags: ["设计顾问", "方案评审", "施工监督", "品质管控"],
    tagsEn: ["Design Consultancy", "Scheme Review", "Construction Supervision", "Quality Control"],
    image: "https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/e0d72b7328ca505e8df6389829afe31c.jpeg",
    relatedProjectIds: [8, 5],
  },
  {
    id: "05",
    title: "酒店、餐饮、会所及住宅设计",
    titleEn: "Hotel, F&B, Club & Residential Design",
    desc: "以人为本，以光为媒。我们深耕酒店、私人会所与高端住宅领域，通过对材质、比例与光影的精细把控，为每一位居住者与访客打造兼具美学深度与生活温度的空间体验。",
    descEn: "Human-centered, light-guided. We specialize in hotels, private clubs, and high-end residences — crafting spatial experiences that balance aesthetic depth with the warmth of living through meticulous control of material, proportion, and light.",
    tags: ["酒店设计", "私人会所", "高端住宅", "软装搭配"],
    tagsEn: ["Hotel Design", "Private Club", "High-end Residence", "Soft Furnishing"],
    image: "https://readdy.ai/api/search-image?query=Luxury%20boutique%20hotel%20lobby%20interior%2C%20double-height%20space%20with%20dramatic%20statement%20lighting%20installation%2C%20natural%20stone%20floor%2C%20warm%20wood%20paneling%2C%20premium%20hospitality%20atmosphere%2C%20sophisticated%20modern%20design%2C%20architectural%20interior%20photography&width=800&height=600&seq=s05v2&orientation=landscape",
    relatedProjectIds: [4, 1],
  },
  {
    id: "06",
    title: "建筑及空间设计",
    titleEn: "Architecture & Space Design",
    desc: "从城市尺度到建筑单体，我们以诗意的设计语言回应场地、气候与文化的多重语境，创造在时间维度上经得起检验的建筑作品。",
    descEn: "From urban scale to individual buildings, we respond to the multiple contexts of site, climate, and culture with a poetic design language — creating architectural works that stand the test of time.",
    tags: ["建筑设计", "文化建筑", "城市更新", "空间规划"],
    tagsEn: ["Architectural Design", "Cultural Building", "Urban Renewal", "Space Planning"],
    image: "https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/4b9b750c66a14eedf2ffda552350189a.jpeg",
    relatedProjectIds: [3],
  },
];

function RelatedProjectCard({ project }: { project: Project }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="group cursor-pointer flex gap-4 items-start"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative w-20 h-20 shrink-0 overflow-hidden">
        <img
          src={project.image}
          alt={project.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        {project.hoverImage && (
          <img
            src={project.hoverImage}
            alt={project.name}
            loading="lazy"
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${hovered ? "opacity-100" : "opacity-0"}`}
          />
        )}
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <p
          className="text-sm font-semibold text-stone-800 group-hover:text-amber-700 transition-colors duration-300 truncate"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {project.name}
        </p>
        <p className="text-xs text-stone-400 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {project.location}
        </p>
        <p className="text-xs text-stone-300 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {project.year}
        </p>
      </div>
      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-1 text-stone-300 group-hover:text-amber-700 transition-colors duration-300">
        <i className="ri-arrow-right-up-line text-sm" />
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = services[activeIdx];

  const relatedProjects: Project[] = active.relatedProjectIds
    .map((id) => projectsData.find((p) => p.id === id))
    .filter((p): p is Project => Boolean(p));

  return (
    <section id="services" className="py-28 bg-stone-50">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-20">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
          <div>
            <p className="text-xs tracking-[0.35em] text-stone-400 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              WHAT WE DO · 我们的服务
            </p>
            <h2 className="text-5xl lg:text-6xl font-black text-stone-900 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Our Design<br />
              <em className="not-italic text-amber-700">Disciplines</em>
            </h2>
          </div>
          <div className="max-w-sm">
            <p className="text-sm text-stone-500 leading-relaxed mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              六大设计方向，覆盖从零售终端到建筑本体的完整设计链路，为品牌与空间创造持久价值。
            </p>
            <p className="text-xs text-stone-400 leading-relaxed italic" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Six design disciplines covering the complete chain from retail to architecture, creating lasting value for brands and spaces.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Left: Service List */}
          <div className="lg:col-span-2 border-t border-stone-200">
            {services.map((svc, idx) => (
              <button
                key={svc.id}
                onClick={() => setActiveIdx(idx)}
                className={`w-full text-left border-b border-stone-200 px-0 py-6 group transition-all duration-300 cursor-pointer flex items-start gap-5 ${
                  activeIdx === idx ? "bg-transparent" : "hover:bg-white/60"
                }`}
              >
                <span
                  className={`text-xs mt-1 transition-colors duration-300 shrink-0 ${activeIdx === idx ? "text-amber-700" : "text-stone-300"}`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {svc.id}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3
                        className={`text-base font-semibold transition-colors duration-300 ${activeIdx === idx ? "text-stone-900" : "text-stone-500 group-hover:text-stone-800"}`}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {svc.title}
                      </h3>
                    </div>
                    <div className={`w-5 h-5 flex items-center justify-center transition-all duration-300 shrink-0 ml-2 ${activeIdx === idx ? "text-amber-700 translate-x-1" : "text-stone-300"}`}>
                      <i className="ri-arrow-right-line text-sm" />
                    </div>
                  </div>
                  <p className="text-xs text-stone-400 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {svc.titleEn}
                  </p>
                  <div className={`mt-3 h-px bg-amber-700 transition-all duration-500 ${activeIdx === idx ? "w-full" : "w-0"}`} />
                </div>
              </button>
            ))}
          </div>

          {/* Right: Detail Panel */}
          <div className="lg:col-span-3 lg:pl-16 mt-10 lg:mt-0">
            <div className="sticky top-28">
              {/* Image */}
              <div className="w-full h-[280px] lg:h-[320px] overflow-hidden mb-8">
                <img
                  key={active.id}
                  src={active.image}
                  alt={active.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top transition-opacity duration-500"
                />
              </div>

              {/* Content */}
              <div key={active.id + "-content"}>
                <div className="flex items-baseline gap-4 mb-4 flex-wrap">
                  <span className="text-xs text-amber-700 tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {active.id}
                  </span>
                  <h3 className="text-3xl font-black text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {active.title}
                  </h3>
                  <span className="text-sm text-stone-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {active.titleEn}
                  </span>
                </div>

                <p className="text-sm text-stone-500 leading-relaxed mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {active.desc}
                </p>
                <p className="text-xs text-stone-400 leading-relaxed italic mb-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {active.descEn}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {active.tags.map((tag, i) => (
                    <span
                      key={tag}
                      className="text-xs text-stone-600 border border-stone-200 px-3 py-1.5 bg-white"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {tag}
                      <span className="text-stone-400 ml-1">/ {active.tagsEn[i]}</span>
                    </span>
                  ))}
                </div>

                {/* Related Projects */}
                {relatedProjects.length > 0 && (
                  <div>
                    <p className="text-xs tracking-[0.25em] text-stone-400 mb-4 uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      代表案例 · Representative Projects
                    </p>
                    <div className="space-y-4">
                      {relatedProjects.map((proj) => (
                        <RelatedProjectCard key={proj.id} project={proj} />
                      ))}
                    </div>
                    <a
                      href="#projects"
                      className="inline-flex items-center gap-2 mt-6 text-xs tracking-[0.15em] text-stone-500 hover:text-amber-700 transition-colors duration-300 cursor-pointer"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      VIEW ALL PROJECTS · 查看全部项目
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs" />
                      </div>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SEO Text Block */}
        <div className="mt-20 pt-16 border-t border-stone-200">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <p className="text-sm text-stone-500 leading-relaxed mb-3"
                 style={{ fontFamily: "'DM Sans', sans-serif" }}>
                作为一家扎根上海、服务全球的建筑与室内设计事务所，WEXTIN(YEAHSTUDIO)的六大设计方向覆盖了从零售终端到建筑本体的完整设计链路。我们的零售设计团队深度介入品牌门店的陈列逻辑与消费者动线，帮助品牌在每一个接触点上实现最有力的表达；品牌空间及标准设计服务则为连锁品牌提供从旗舰店到标准店的全套 SI 体系；商业设计涵盖购物中心、办公楼宇与文娱场所，以精准的功能布局创造持久的商业价值。
              </p>
              <p className="text-xs text-stone-400 leading-relaxed italic mb-3"
                 style={{ fontFamily: "'DM Sans', sans-serif" }}>
                As an architecture and interior design practice rooted in Shanghai and serving clients nationwide, YEAHSTUDIO&apos;s six design disciplines cover the complete design chain from retail terminals to architectural structures. Our retail design team deeply engages with brand store display logic and consumer circulation, helping brands achieve their most powerful expression at every touchpoint; our brand space and standard design services provide complete SI systems from flagship to standard stores; commercial design spans shopping centers, office buildings, and entertainment venues, creating lasting commercial value through precise functional layouts.
              </p>
              <p className="text-sm text-stone-500 leading-relaxed mb-3"
                 style={{ fontFamily: "'DM Sans', sans-serif" }}>
                在酒店、餐饮与住宅领域，我们以光为媒，通过对材质比例与光影关系的精细把控，为高端客户打造兼具美学深度与生活温度的私人空间。建筑及空间设计方向则从城市尺度出发，以诗意的设计语言回应场地、气候与文化的多重语境，创造经得起时间检验的建筑作品。
              </p>
              <p className="text-xs text-stone-400 leading-relaxed italic"
                 style={{ fontFamily: "'DM Sans', sans-serif" }}>
                In the hotel, F&amp;B, and residential sectors, we use light as our medium — through meticulous control of material proportion and light-shadow relationships, we craft private spaces for premium clients that balance aesthetic depth with the warmth of living. Our architecture and space design discipline operates at urban scale, responding to the multiple contexts of site, climate, and culture with a poetic design language to create architectural works that stand the test of time.
              </p>
            </div>
            <div className="space-y-4">
              <div className="border-l-2 border-amber-700 pl-5">
                <p className="text-xs tracking-[0.2em] text-stone-400 mb-1"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  SERVICE AREA · 服务范围
                </p>
                <p className="text-sm text-stone-600 leading-relaxed"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  全球
                </p>
                <p className="text-xs text-stone-400 italic mt-1"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Global
                </p>
              </div>
              <div className="border-l-2 border-stone-200 pl-5">
                <p className="text-xs tracking-[0.2em] text-stone-400 mb-1"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  PROJECT SCALE · 项目规模
                </p>
                <p className="text-sm text-stone-600 leading-relaxed"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  50㎡ 精品门店至 50,000㎡ 大型综合体，全规模覆盖
                </p>
                <p className="text-xs text-stone-400 italic mt-1"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  From 50㎡ boutique stores to 50,000㎡ large-scale complexes — full scale coverage
                </p>
              </div>
              <div className="border-l-2 border-stone-200 pl-5">
                <p className="text-xs tracking-[0.2em] text-stone-400 mb-1"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  WORKING HOURS · 工作时间
                </p>
                <p className="text-sm text-stone-600"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  周一至周五 09:00 – 18:00
                </p>
                <p className="text-xs text-stone-400 italic mt-1"
                   style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Monday – Friday, 09:00 – 18:00
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
